import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { TrizApi } from '../../apis/triz-api';
import type { TrizService } from '../triz-service';
import type { fetchStepRequest, RegenerateIdeaRequest, GenerateExtendIdeaRequest, FetchVideoResultRequest, ExportPptRequest, ExportPptPreviewRequest, ExportPptPreviewResponse, ModuleData, StepData, StepsData, VideoSubmitRequest, VideoResultResponse, ImageSubmitRequest, ImageResultResponse } from '../../types';
import { ref, computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import { get } from 'lodash-es';
import { MODULE_CONTENT_KEY, type ISolution } from '@/domains/chat/types';
import type { PatentRecallEvaluationMessageData, ModuleContentMessageData } from '@/domains/chat/types/triz-events';

@injectable()
export class TrizServiceImpl implements TrizService {
    constructor(
        @inject(TrizApi) private trizApi: TrizApi
    ) {
    }

    /** 主存储：version_number -> (module_key -> ModuleData) */
    private moduleDataMap = ref<Map<number, Map<string, ModuleData<any>>>>(new Map());
    /** 版本元数据：version_number -> { id, business_id } */
    private versionMeta = ref<Map<number, { id: number; business_id: string }>>(new Map());

    /** 详情数据：idea_id -> ModuleData (SOLUTION_REFINE_ANALYSIS) */
    private solutionDetailMap = ref<Map<string, ModuleData<any>>>(new Map());
    /** 图片数据：idea_id -> ModuleData (SOLUTION_IMAGE) */
    private solutionImageMap = ref<Map<string, ModuleData<any>>>(new Map());
    /** 视频数据：idea_id -> ModuleData (SOLUTION_VIDEO) */
    private solutionVideoMap = ref<Map<string, ModuleData<any>>>(new Map());
    /** 流程图数据：idea_id -> ModuleData (SOLUTION_FLOWCHART) */
    private solutionFlowchartMap = ref<Map<string, ModuleData<any>>>(new Map());

    fetchStepsData(data: fetchStepRequest) {
        return this.trizApi.fetchStepsData(data).then(
            (result) => {
                this.updateStepsData(result.modules);
                return result.modules;
            }
        );
    }

    regenerateIdea(data: RegenerateIdeaRequest) {
        return this.trizApi.regenerateIdea(data);
    }

    updateStepsData(modules: StepsData): void {
        if (modules.length === 0) {
            this.moduleDataMap.value.clear();
            this.versionMeta.value.clear();
        }
    }

    setSolutionDetail(moduleData: ModuleData<any>) {
        const ideaId = get(moduleData, 'message.idea_id');
        if (ideaId != null) {
            this.solutionDetailMap.value.set(String(ideaId), moduleData);
        }
    }

    getSolutionDetail(ideaId: string): ModuleData<any> | undefined {
        return this.solutionDetailMap.value.get(ideaId);
    }

    setSolutionImage(moduleData: ModuleData<any>) {
        const ideaId = get(moduleData, 'message.idea_id');
        if (ideaId != null) {
            this.solutionImageMap.value.set(String(ideaId), moduleData);
        }
    }

    getSolutionImage(ideaId: string): ModuleData<any> | undefined {
        return this.solutionImageMap.value.get(ideaId);
    }

    setSolutionVideo(moduleData: ModuleData<any>) {
        const ideaId = get(moduleData, 'message.idea_id');
        if (ideaId != null) {
            this.solutionVideoMap.value.set(String(ideaId), moduleData);
        }
    }

    getSolutionVideo(ideaId: string): ModuleData<any> | undefined {
        return this.solutionVideoMap.value.get(ideaId);
    }

    setSolutionFlowchart(moduleData: ModuleData<any>) {
        const ideaId = get(moduleData, 'message.idea_id');
        if (ideaId != null) {
            this.solutionFlowchartMap.value.set(String(ideaId), moduleData);
        }
    }

    getSolutionFlowchart(ideaId: string): ModuleData<any> | undefined {
        return this.solutionFlowchartMap.value.get(ideaId);
    }

    getModuleData(versionNumber: number, moduleKey: string): ModuleData<any> | undefined {
        return this.moduleDataMap.value.get(versionNumber)?.get(moduleKey);
    }

    getModuleDataRef(version: MaybeRefOrGetter<number>, moduleKey: string) {
        return computed(() => this.moduleDataMap.value.get(toValue(version))?.get(moduleKey));
    }

    setModuleData(versionNumber: number, moduleKey: string, moduleData: ModuleData<any>): void {
        let versionMap = this.moduleDataMap.value.get(versionNumber);
        if (!versionMap) {
            versionMap = new Map();
            this.moduleDataMap.value.set(versionNumber, versionMap);
        }
        if (moduleKey === MODULE_CONTENT_KEY.SOLUTION_REFINE_ANALYSIS) {
            this.setSolutionDetail(moduleData);
        } else if (moduleKey === MODULE_CONTENT_KEY.SOLUTION_IMAGE) {
            this.setSolutionImage(moduleData);
        } else if (moduleKey === MODULE_CONTENT_KEY.SOLUTION_VIDEO) {
            this.setSolutionVideo(moduleData);
        } else if (moduleKey === MODULE_CONTENT_KEY.SOLUTION_FLOWCHART) {
            this.setSolutionFlowchart(moduleData);
        } else {
            versionMap.set(moduleKey, moduleData);
        }
    }

    updateStepModuleStatus(versionNumber: number, moduleKey: string, ifEnd: boolean): void {
        const existing = this.getModuleData(versionNumber, moduleKey);
        if (existing) {
            this.setModuleData(versionNumber, moduleKey, { ...existing, ifEnd } as ModuleData<any>);
        }
    }

    getVersionMeta(versionNumber: number): { id: number; business_id: string } | undefined {
        return this.versionMeta.value.get(versionNumber);
    }

    /**
     * 根据 versionNumber 和 moduleKeys 聚合模块数据（兼容 Step 组件所需的 data 结构）
     */
    getModulesData(versionNumber: number, moduleKeys: string[]): StepData {
        const versionMap = this.moduleDataMap.value.get(versionNumber);
        const meta = this.versionMeta.value.get(versionNumber);
        const result: StepData = {
            id: meta?.id ?? 0,
            step_id: 0,
            business_id: meta?.business_id ?? '',
            current_module_key: '',
        };
        moduleKeys.forEach((moduleKey) => {
            const moduleData = versionMap?.get(moduleKey);
            if (moduleData) {
                result[moduleKey] = moduleData;
            }
        });
        return result;
    }

    clearVersionData(versionNumber: number): void {
        this.moduleDataMap.value.delete(versionNumber);
        this.versionMeta.value.delete(versionNumber);
    }

    generateExtendIdea(data: GenerateExtendIdeaRequest): Promise<any> {
        return this.trizApi.generateExtendIdea(data);
    }

    /**
     * 合并专利召回数据
     * @param evaluationData 专利评估消息数据
     */
    mergePatentRecallData(evaluationData: PatentRecallEvaluationMessageData): void {
        const { version_number, module_key, message } = evaluationData.data;
        const { key_problem, relevant_patents } = message;
        const problemId = key_problem?.problem_id;

        const existingData = this.getModuleData(version_number, module_key);
        // grouped: { [problem_id]: { key_problem, relevant_patents: ISolution[] } }
        const grouped: Record<string, { key_problem: typeof key_problem; relevant_patents: ISolution[] }> =
            get(existingData, 'message.grouped', {});

        if (problemId) {
            const existing = grouped[problemId]?.relevant_patents ?? [];
            const merged = Array.from(
                [...existing, ...relevant_patents]
                    .reduce((map, p) => map.set(p.PATENT_ID, p), new Map<string, ISolution>())
                    .values()
            );
            grouped[problemId] = { key_problem, relevant_patents: merged };
        }

        this.setModuleData(version_number, module_key, {
            ...evaluationData.data,
            role: '',
            message: { grouped },
        } as ModuleData<any>);
    }

    mergeSolutionIdeas(message: ModuleContentMessageData): void {
        const { version_number, module_key, message: msgList } = message.data;
        const existingData = this.getModuleData(version_number, module_key);
        const grouped: Record<string, { key_problem: any; idea_list: any[]; used_patent_ids: string[] }> =
            get(existingData, 'message.grouped', {});

        const items = Array.isArray(msgList) ? msgList : [msgList];
        for (const item of items) {
            const problemId = item?.key_problem?.problem_id;
            if (!problemId) continue;
            const existing = grouped[problemId]?.idea_list ?? [];
            const merged = Array.from(
                [...existing, ...(item.idea_list ?? [])]
                    .reduce((map, idea) => map.set(idea.idea_id, idea), new Map<string, any>())
                    .values()
            );
            grouped[problemId] = {
                key_problem: item.key_problem,
                idea_list: merged,
                used_patent_ids: item.used_patent_ids ?? [],
            };
        }

        this.setModuleData(version_number, module_key, {
            ...message.data,
            role: '',
            message: { grouped },
        } as ModuleData<any>);
    }

    submitVideo(data: VideoSubmitRequest): Promise<any> {
        return this.trizApi.submitVideo(data);
    }

    fetchVideoResult(data: FetchVideoResultRequest): Promise<VideoResultResponse> {
        return this.trizApi.fetchVideoResult(data);
    }

    submitImage(data: ImageSubmitRequest): Promise<any> {
        return this.trizApi.submitImage(data);
    }

    fetchImageResult(data: { triz_mind_id: string; idea_id: string }): Promise<ImageResultResponse> {
        return this.trizApi.fetchImageResult(data);
    }

    exportPpt(data: ExportPptRequest): Promise<any> {
        return this.trizApi.exportPpt(data);
    }

    fetchExportPptPreview(data: ExportPptPreviewRequest): Promise<ExportPptPreviewResponse> {
        return this.trizApi.fetchExportPptPreview(data);
    }
}
